import { __assign, __read, __spreadArray } from "tslib";
import { findIndex, get, uniqWith } from 'lodash-es';
import * as handoffState from '../constants/handoffState';
import * as requestStatus from '../constants/requestStatus';
import * as zoom from '../constants/zoom';
import * as convert from '../utils/convertColours';
import { compareColors, getColorFills } from '../utils/handoff/colors';
import { getElementColors, getElementLayout } from '../utils/handoff/element';
import * as tree from '../utils/handoff/tree';
import { getCurrentScreen, getRenderedScreenDimensions } from './screen';
export function hasHandoffData(state) {
    return !!getCurrentScreen(state).canvasID;
}
export function getCurrentCanvasID(state) {
    return get(state, 'handoff.canvas.id');
}
export function getArtboardTitle(state) {
    return get(state, 'handoff.canvas.title', 'Handoff');
}
export function getArtboardSize(state) {
    return get(state, 'handoff.canvas.artboard.size');
}
export function getDocumentColors(state) {
    var allColors = state.handoff.flatCanvas.reduce(function (colors, element) { return colors.concat(getElementColors(element)); }, []);
    allColors.push.apply(allColors, __spreadArray([], __read(getArtboard(state).fills.map(function (c) { return c.color; })), false));
    var opacityCols = [];
    var solidCols = [];
    allColors.forEach(function (col) {
        return convert.hasOpacity(col) ? opacityCols.push(col) : solidCols.push(col);
    });
    solidCols = getUniqueNonEmptyColors(solidCols, convert.rgbArray2css);
    opacityCols = getUniqueNonEmptyColors(opacityCols, convert.rgbaArray2rgba);
    solidCols.sort(compareColors);
    opacityCols.sort(compareColors);
    return { solidCols: solidCols, opacityCols: opacityCols };
}
function getUniqueNonEmptyColors(colors, compareValueFunc) {
    return uniqWith(colors, function (a, b) { return compareValueFunc(a) === compareValueFunc(b); }).filter(function (e) { return e.length; });
}
export function isInHandoffMode(state) {
    return state.handoff.isOpen;
}
export function isInitialHandoffZoomSet(state) {
    return state.handoff.initialZoomSet;
}
export function getZoomScale(state) {
    return getZoomPercentage(state) / 100;
}
export function getInitialZoom(state, availableWidth) {
    var renderedDimensions = getRenderedScreenDimensions(state);
    var imageWidth = get(state, 'screen.currentScreen.img.width');
    if (imageWidth && renderedDimensions) {
        if (imageWidth <= availableWidth) {
            return zoom.getPositionForPercentage(100);
        }
        return getFittedZoomPosition(imageWidth, availableWidth);
    }
    return zoom.DEFAULT_POSITION;
}
export function getZoomPosition(state) {
    return state.handoff.zoomPosition;
}
function getFittedZoomPosition(imageWidth, availableWidth) {
    var fittedZoom = (availableWidth * 100) / imageWidth;
    var closestZoom = 0;
    for (var i = 0; i < zoom.LEVELS.length; i += 1) {
        if (zoom.LEVELS[i] <= fittedZoom) {
            closestZoom = i;
        }
        else {
            break;
        }
    }
    return closestZoom;
}
export function getElements(state) {
    if (!isInHandoffMode(state) ||
        !isHandoffAvailable(state) ||
        !state.handoff.flatCanvas) {
        return [];
    }
    return state.handoff.flatCanvas;
}
export function getElementHotspots(state) {
    return getElements(state).map(function (element, index) { return (__assign({ id: index }, getElementLayout(element))); });
}
export function isElementSelected(state, elementIndex) {
    return state.handoff.selectedItemIndex === elementIndex;
}
export function isElementHovered(state, elementIndex) {
    return state.handoff.hoveredItemIndex === elementIndex;
}
export function getElementClassName(state, elementIndex) {
    return state.handoff.flatCanvas[elementIndex].className;
}
export function isElementHoverable(state, elementIndex) {
    var _a = state.handoff, selectedItemIndex = _a.selectedItemIndex, hoveredItemIndex = _a.hoveredItemIndex;
    return ((selectedItemIndex !== null && selectedItemIndex !== elementIndex) ||
        hoveredItemIndex === null);
}
export function isElementSelectable(state, index) {
    var actual = getSelectedElementIndex(state, index);
    return actual === index;
}
export function getSelectedElementIndex(state, clickedElementIndex) {
    var clicked = state.handoff.flatCanvas[clickedElementIndex];
    var current = state.handoff.flatCanvas[state.handoff.selectedItemIndex];
    if (tree.isElementNested(clicked) &&
        !tree.isElementInSameTopLevelGroupAsCurrentSelectionAndNoDeeper(current, clicked)) {
        var selected = tree.getAncestorOfElementThatIsChildOfCurrentSelection(current, clicked);
        if (!selected) {
            selected = tree.getTopLevelGroup(clicked);
        }
        /* eslint-disable no-param-reassign */
        var newIndex = getElementIndex(state, selected);
        return newIndex;
    }
    return clickedElementIndex;
}
function getElementIndex(state, element) {
    return findIndex(state.handoff.flatCanvas, function (el) { return el === element; });
}
export function getSelectedElement(state) {
    if (state.handoff.selectedItemIndex === null) {
        return null;
    }
    return state.handoff.flatCanvas[state.handoff.selectedItemIndex];
}
export function getSelectedElementType(state) {
    var selectedElement = getSelectedElement(state);
    if (!selectedElement) {
        return 'default';
    }
    return selectedElement.className.toLowerCase();
}
export function getSelectedElementLayout(state) {
    var element = getSelectedElement(state);
    if (!element) {
        return {};
    }
    return getElementLayout(element);
}
export function getHoveredElement(state) {
    if (state.handoff.hoveredItemIndex === null) {
        return null;
    }
    return state.handoff.flatCanvas[state.handoff.hoveredItemIndex];
}
export function getHoveredElementLayout(state) {
    var element = getHoveredElement(state);
    if (!element) {
        return {};
    }
    return getElementLayout(element);
}
export function getElementLayoutForPercentage(state) {
    var artboard = getArtboard(state);
    var selected = getSelectedElement(state);
    var hovered = getHoveredElement(state);
    return tree.getElementLayoutForPercentage(artboard, selected, hovered);
}
export function showGuides(state, id) {
    var _a = state.handoff, selectedItemIndex = _a.selectedItemIndex, hoveredItemIndex = _a.hoveredItemIndex;
    return (isElementSelectedAndNoneHovered(selectedItemIndex, hoveredItemIndex, id) ||
        isElementHoveredAndNoneSelected(selectedItemIndex, hoveredItemIndex, id) ||
        isSameElementSelectedAndHovered(selectedItemIndex, hoveredItemIndex, id));
}
export function showRelativeGuides(state) {
    var _a = state.handoff, hoveredItemIndex = _a.hoveredItemIndex, selectedItemIndex = _a.selectedItemIndex;
    return (hoveredItemIndex !== null &&
        selectedItemIndex !== null &&
        selectedItemIndex !== hoveredItemIndex);
}
function isElementSelectedAndNoneHovered(selectedItemIndex, hoveredItemIndex, id) {
    return selectedItemIndex === id && hoveredItemIndex === null;
}
function isElementHoveredAndNoneSelected(selectedItemIndex, hoveredItemIndex, id) {
    return hoveredItemIndex === id && selectedItemIndex === null;
}
function isSameElementSelectedAndHovered(selectedItemIndex, hoveredItemIndex, id) {
    return hoveredItemIndex === id && selectedItemIndex === id;
}
// TODO Mock properly in tests
export function getHandoffState(state) {
    if (process.env.NODE_ENV === 'test') {
        return handoffState.AVAILABLE;
    }
    if (!hasHandoffData(state)) {
        return handoffState.NO_HANDOFF_DATA;
    }
    if (state.handoff.requestStatus === requestStatus.ERROR) {
        return handoffState.REQUEST_ERROR;
    }
    return handoffState.AVAILABLE;
}
export function isHandoffAvailable(state) {
    return getHandoffState(state) === handoffState.AVAILABLE;
}
export function isHandoffLoading(state) {
    return state.handoff.requestStatus === requestStatus.PENDING;
}
export function getEaselConfig(state) {
    return {
        canvasEaselHost: state.project.canvasEaselHost,
    };
}
export function getUnit(state) {
    return state.handoff.unit;
}
export function getColorMode(state) {
    return state.handoff.colorMode;
}
export function getPixelRatio(state) {
    return get(state, 'handoff.canvas.artboard.pixelRatio', 1);
}
export function getArtboard(state) {
    if (!state.handoff.canvas) {
        return {};
    }
    var artboard = state.handoff.canvas.artboard;
    var data = {
        width: artboard.size[0],
        height: artboard.size[1],
        fills: getColorFills(get(artboard, 'fills', [])),
    };
    return data;
}
export function getZoomPercentage(state) {
    return zoom.getZoomPercentage(state.handoff.zoomPosition);
}
export function getZoomFit(state) {
    return state.handoff.zoomFit;
}
export function arePercentageBasedGuidelinesEnabled(state) {
    return state.handoff.percentageBasedGuidelines;
}
export function getSelectedItemUUID(state) {
    var _a;
    return (_a = getSelectedElement(state)) === null || _a === void 0 ? void 0 : _a.__unsafeUUID;
}
